/**
 * Pricing Table - Responsive table with sticky header and plan box alignment
 *
 * Used by: pricing_plan.blade.php
 */

declare function sendGTMEvent(eventId: string): void;

const MOBILE_BREAKPOINT = 576;

interface TableBounds {
    top: number;
    bottom: number;
}

function initPricingPlan(component: HTMLElement): void {
    const table = component.querySelector<HTMLTableElement>('.feature-table');
    const thead = table?.querySelector('thead');
    const ths = thead?.querySelectorAll<HTMLTableCellElement>('th');

    if (!table || !thead || !ths) {
        return;
    }

    let isSticky = false;
    let lastScrollTop = 0;
    let tableBounds: TableBounds = { top: 0, bottom: 0 };
    let ticking = false;

    /**
     * Aligns plan boxes (e.g., Basic vs Pro) so they appear symmetrical 
     * regardless of varying content lengths.
     */
    const alignPlanBoxes = (): void => {
        const basicBox = component.querySelector<HTMLElement>('.basic-box');
        const basicBoxTop = component.querySelector<HTMLElement>('.basic-box-top');
        const proBoxTop = component.querySelector<HTMLElement>('.pro-box-top');

        if (basicBox && basicBoxTop && proBoxTop) {
            const topDifference = proBoxTop.offsetHeight - basicBoxTop.offsetHeight;
            basicBox.style.marginTop = `${topDifference}px`;
        }

        const basicBoxBottom = component.querySelector<HTMLElement>('.basic-box-bottom-text');
        const proBoxBottom = component.querySelector<HTMLElement>('.pro-box-bottom-text');

        if (basicBoxBottom && proBoxBottom) {
            const boxDifference = basicBoxBottom.offsetHeight - proBoxBottom.offsetHeight;

            if (boxDifference < 0) {
                const newHeight = basicBoxBottom.offsetHeight - boxDifference;
                basicBoxBottom.style.height = `${newHeight}px`;
            } else {
                const newHeight = proBoxBottom.offsetHeight + boxDifference;
                proBoxBottom.style.height = `${newHeight}px`;
            }
        }
    };

    /**
     * On mobile, standard tables are hard to read. This collapses 
     * feature columns to maximize space for row labels.
     */
    const adjustTableOnMobile = (): void => {
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
        const rows = component.querySelectorAll<HTMLTableRowElement>('table tbody tr:not(.subfeatures):not(.mobile)');

        rows.forEach((row) => {
            const firstCell = row.querySelector<HTMLTableCellElement>('td:first-child');
            const allCells = row.querySelectorAll<HTMLTableCellElement>('td');

            if (isMobile === true) {
                if (firstCell) {
                    firstCell.setAttribute('colspan', String(allCells.length));
                }

                allCells.forEach((cell, index) => {
                    if (index > 0) {
                        cell.style.display = 'none';
                    }
                });
            } else {
                if (firstCell) {
                    firstCell.removeAttribute('colspan');
                }

                allCells.forEach((cell) => {
                    cell.style.display = '';
                });
            }
        });
    };

    /**
     * Caches DOM positions to avoid expensive layout thrashing 
     * inside the scroll listener.
     */
    const cacheTableBounds = (): void => {
        const rect = table.getBoundingClientRect();

        tableBounds = {
            top: rect.top + window.scrollY,
            bottom: rect.top + window.scrollY + table.offsetHeight
        };
    };

    /**
     * Preserves column widths before making header sticky.
     */
    const preserveColumnWidths = (): void => {
        const widths = Array.from(ths).map((th) => {
            return th.offsetWidth;
        });

        ths.forEach((th, index) => {
            th.style.width = `${widths[index]}px`;
        });
    };

    /**
     * Resets column widths when header is no longer sticky.
     */
    const resetColumnWidths = (): void => {
        ths.forEach((th) => {
            th.style.width = '';
        });
    };

    /**
     * Updates the sticky state of the table header based on scroll position.
     */
    const updateStickyState = (): void => {
        if (window.innerWidth > MOBILE_BREAKPOINT) {
            if (isSticky === true) {
                thead.classList.remove('sticky');
                resetColumnWidths();
                isSticky = false;
            }

            return;
        }

        const scrollTop = window.scrollY;
        const isScrollingUp = scrollTop < lastScrollTop;
        lastScrollTop = scrollTop;

        const tableVisible = scrollTop + window.innerHeight > tableBounds.top && scrollTop < tableBounds.bottom;
        const appearOffset = isScrollingUp ? 20 : 0;
        const disappearOffset = isScrollingUp ? 0 : 60;
        const pastThead = scrollTop > tableBounds.top - appearOffset && scrollTop < (tableBounds.bottom - thead.offsetHeight - disappearOffset);

        if (tableVisible === true && pastThead === true && isSticky === false) {
            preserveColumnWidths();
            thead.classList.add('sticky');
            isSticky = true;
        } else if ((tableVisible === false || pastThead === false) && isSticky === true) {
            thead.classList.remove('sticky');
            resetColumnWidths();
            isSticky = false;
        }
    };

    /**
     * RequestAnimationFrame throttle to keep scroll performance smooth.
     */
    const onScroll = (): void => {
        if (ticking === false) {
            window.requestAnimationFrame(() => {
                updateStickyState();
                ticking = false;
            });
            ticking = true;
        }
    };

    /**
     * Resize handler: re-adjusts layout and recalculates bounds.
     */
    const handleResize = (): void => {
        adjustTableOnMobile();
        cacheTableBounds();
        resetColumnWidths();
        updateStickyState();
    };

    // Tracking for collapsible feature groups
    const collapsibles = component.querySelectorAll<HTMLElement>('table tbody [data-bs-toggle="collapse"]');
    collapsibles.forEach((el) => {
        el.addEventListener('click', function(this: HTMLElement) {
            const gtmId = this.dataset.gtmId;

            if (gtmId && typeof sendGTMEvent === 'function') {
                sendGTMEvent(gtmId);
            }
        });
    });

    // Initialize
    alignPlanBoxes();
    adjustTableOnMobile();
    cacheTableBounds();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', handleResize);
}

// Auto-initialize all pricing plan components
document.addEventListener('DOMContentLoaded', () => {
    const components = document.querySelectorAll<HTMLElement>('.mp-component-pricing-plan');
    components.forEach((component) => {
        initPricingPlan(component);
    });
});

export { initPricingPlan };